import { WS_URL } from '../utils/config';

const RECONNECT_DELAY = 1500;
const HEARTBEAT_INTERVAL = 5000;
const STALE_CONNECTION_TIMEOUT = 14000;

class WSClient {
  constructor() {
    this.ws = null;
    this.clientId = null;
    this.listeners = new Map();
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.isConnecting = false;
    this.lastActivityAt = 0;
    this.lifecycleBound = false;

    this._bindLifecycleEvents();
  }

  _bindLifecycleEvents() {
    if (this.lifecycleBound || typeof window === 'undefined') return;
    this.lifecycleBound = true;

    const wakeUp = () => {
      if (!this.clientId) return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this._forceReconnect();
        return;
      }
      // Probe the socket immediately when returning from background / iOS Safari suspend
      try {
        this.ws.send(JSON.stringify({ type: 'ping', clientId: this.clientId }));
      } catch {
        this._forceReconnect();
      }
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        wakeUp();
      }
    });
    window.addEventListener('pageshow', wakeUp);
    window.addEventListener('focus', wakeUp);
    window.addEventListener('online', wakeUp);
  }

  connect(clientId, force = false) {
    if (!clientId) return;

    if (force) {
      this.clientId = clientId;
      this._forceReconnect();
      return;
    }

    if (
      this.isConnecting ||
      (this.ws && this.ws.readyState === WebSocket.OPEN && this.clientId === clientId)
    ) {
      return;
    }

    this.clientId = clientId;
    this.isConnecting = true;
    clearTimeout(this.reconnectTimer);

    try {
      if (this.ws) {
        try {
          this.ws.onclose = null;
          this.ws.close();
        } catch {
          // ignore
        }
      }

      this.ws = new WebSocket(WS_URL);

      this.ws.onopen = () => {
        this.isConnecting = false;
        this.lastActivityAt = Date.now();
        console.log('[WS] Connected');
        this.send({ type: 'register', clientId: this.clientId });
        this._startHeartbeat();
        this._emit('connected');
      };

      this.ws.onmessage = (event) => {
        this.lastActivityAt = Date.now();
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] Message received:', data);
          if (data?.type === 'pong') return;
          this._emit('message', data);
          if (data.action) {
            this._emit(data.action, data);
          }
          if (data.status) {
            this._emit(`status:${data.status}`, data);
          }
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      this.ws.onclose = () => {
        this.isConnecting = false;
        this._stopHeartbeat();
        console.log('[WS] Disconnected, reconnecting...');
        this._emit('disconnected');
        this._scheduleReconnect();
      };

      this.ws.onerror = (err) => {
        this.isConnecting = false;
        console.error('[WS] Error:', err);
      };
    } catch (err) {
      this.isConnecting = false;
      console.error('[WS] Failed to connect:', err);
      this._scheduleReconnect();
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this._forceReconnect();
        return;
      }
      if (this.lastActivityAt && Date.now() - this.lastActivityAt > STALE_CONNECTION_TIMEOUT) {
        console.warn('[WS] Connection stale, forcing reconnect...');
        this._forceReconnect();
        return;
      }
      try {
        this.ws.send(JSON.stringify({ type: 'ping', clientId: this.clientId }));
      } catch {
        this._forceReconnect();
      }
    }, HEARTBEAT_INTERVAL);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  _forceReconnect() {
    clearTimeout(this.reconnectTimer);
    this._stopHeartbeat();
    this.isConnecting = false;
    if (this.ws) {
      try {
        this.ws.onclose = null;
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    if (this.clientId) {
      this.connect(this.clientId);
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    const set = this.listeners.get(event);
    if (set) {
      set.delete(callback);
    }
  }

  _emit(event, data) {
    const set = this.listeners.get(event);
    if (set) {
      set.forEach((cb) => {
        try {
          cb(data);
        } catch (err) {
          console.error('[WS] Listener error:', err);
        }
      });
    }
  }

  _scheduleReconnect() {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      if (this.clientId) {
        this.connect(this.clientId);
      }
    }, RECONNECT_DELAY);
  }

  disconnect() {
    clearTimeout(this.reconnectTimer);
    this._stopHeartbeat();
    this.clientId = null;
    this.isConnecting = false;
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}

export const wsClient = new WSClient();
export default wsClient;
